<script lang="ts">
  import { t, changeLocale, locale, isUrlLocalized, localizeUrl } from '$lib/translations'
  import { LANGS_FOR_DROPDOWN } from '$lib/constants'
  import * as DropdownMenu from '$lib/components/ui/dropdown-menu'
  import { HelpCircle, User2, UserPlus, LogIn } from 'lucide-svelte'
  import type { LangKeys, NavItem } from '$lib/types'
  import MobileMainNav from './MobileMainNav.svelte'
  import { twMerge } from 'tailwind-merge'
  import type { AuthModel } from 'pocketbase'
  import { langValueToIcon, langLabelToValue } from '$lib/utils'
  import { browser } from '$app/environment'

  export let user: AuthModel | null | undefined = undefined

  let classes = ''
  export { classes as class }

  let selectedLangValue: LangKeys = $locale as LangKeys
  $: if (browser && locale.get() !== selectedLangValue) {
    locale.set(selectedLangValue)
    window.location.href = localizeUrl(window.location.pathname, selectedLangValue).toString()
  }

  let navItems: NavItem[] = [
    { path: '/', label: 'common.homepage' },
    { path: '/services', label: 'common.services' },
    // { path: 'https://lanzadera.canaryshuttle.com/', label: 'common.events' }, // Deactivated untill the new events page is ready
    { path: 'https://last-minute.canaryshuttle.com/', label: 'common.lastminute' },
    { path: '/request-estimate', label: 'common.request_an_estimate' },
    { path: '/contact', label: 'common.contact' },
    { path: '/projects', label: 'common.projects'},
    { path: '/register', label: 'common.register', mobileOnly: true  },
    { path: '/login', label: 'common.login', mobileOnly: true }
  ]

  navItems = navItems.map((i) => ({ ...i, path: i.path.startsWith('https://') ? i.path : localizeUrl(i.path, locale.get()).toString() }))

  const homePath = localizeUrl('/', locale.get()).toString()
  const loginPath = localizeUrl('/login', locale.get()).toString()
  const singupPath = localizeUrl('/register', locale.get()).toString()
  const userPath = user ? localizeUrl(`/user/${user.id}`, locale.get()).toString() : ''
  const contactPath = localizeUrl('/contact', locale.get()).toString()
  const logoutPath = localizeUrl('/user/logout', locale.get()).toString()

  function navigate(path: string) {
    if (typeof window !== 'undefined') {
      window.location.href = path
    }
  }
</script>

<header>
  <div
    class={twMerge(
      'bg-white text-black flex flex-row justify-between items-center lg:px-[40px] px-[20px]  py-2 w-full shadow-md',
      classes
    )}
  >
    <a href={homePath} class="hidden lg:flex order-2 lg:order-1">
      <img class="min-h-[64px] h-[64px] py-3" src="/new-logo.webp" alt="Canaryshuttle Logo" />
    </a>
    <a href={homePath} class="lg:hidden order-2 lg:order-1">
      <img class="max-h-20 py-4 px-4 lg:px-0" src="/new-logo.webp" alt="Canaryshuttle Logo" />
    </a>

    <nav id="secondary-nav" class="hidden lg:flex lg:order-3">
      <ul class="flex flex-row gap-5 items-center">
        <li class="flex flex-row">
          {#each LANGS_FOR_DROPDOWN as lang}
            <button class="p-2" on:click={() => (selectedLangValue = langLabelToValue[lang.label])}>
              <svelte:component this={langValueToIcon[lang.label]} size={'20px'} />
            </button>
          {/each}
        </li>
        <li class="flex flex-row gap-1 text-[13px] items-center">
          <HelpCircle size={16} />
          <a href={contactPath} class="uppercase">{$t('common.help')}</a>
        </li>
        {#if user}
          <li class="flex flex-row gap-1 text-[13px] items-center">
            <UserPlus size={16} />
            <button on:click={() => navigate(`${userPath}?section=account`)} class="uppercase">{$t('user.your_account')}</button>
          </li>
          <li class="flex flex-row gap-1 text-[13px] items-center">
            <UserPlus size={16} />
            <button on:click={() => navigate(`${userPath}?section=reservations`)} class="uppercase">{$t('user.your_reservations')}</button>
          </li>
          <li class="flex flex-row gap-1 text-[13px] items-center">
            <User2 size={16} />
            <button on:click={() => navigate(logoutPath)} class="uppercase">{$t('user.log_out')}</button>
          </li>
        {:else}
          <li class="flex flex-row gap-1 text-[13px] items-center">
            <UserPlus size={16} />
            <a href={singupPath} class="uppercase">{$t('common.register')}</a>
          </li>
          <li class="flex flex-row gap-1 text-[13px] items-center">
            <LogIn size={16} />
            <a href={loginPath} class="uppercase">{$t('common.login')}</a>
          </li>
        {/if}
      </ul>
    </nav>
    <nav id="mobile-main-nav" class="flex lg:hidden order-1">
      <MobileMainNav {navItems} bind:selectedLangValue />
    </nav>
    <nav id="mobile-user-nav" class="flex lg:hidden order-3">
      {#if user}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger>
            <span><User2 class="text-cs-orange" fill="#ea591d" size={30} /></span>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content class="w-56 text-white bg-cs-orange">
            <DropdownMenu.Label>{$t('user.hello')} {` ${user.name}`}</DropdownMenu.Label>
            <DropdownMenu.Separator />
            <DropdownMenu.Item on:click={() => navigate(`${userPath}?section=account`)}>
              {$t('user.your_account')}
            </DropdownMenu.Item>
            <DropdownMenu.Item on:click={() => navigate(`${userPath}?section=reservations`)}>
              {$t('user.your_reservations')}
            </DropdownMenu.Item>
            <!-- <DropdownMenu.Item on:click={() => localizeUrl(`${userPath}?section=invoices`, locale.get())}>
              {$t('user.your_invoices')}
            </DropdownMenu.Item> -->
            <DropdownMenu.Separator />
            <DropdownMenu.Item on:click={() => logoutPath}>
              {$t('user.log_out')}
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Root>
      {:else}
        <!-- <a href={loginPath}><User2 class="text-black" size={30} /></a> -->
      {/if}
    </nav>
  </div>
  <nav id="main-nav" class="hidden lg:flex lg:order-2 bg-[#dedede] text-gray-900">
    <ul class="py-2 grid grid-flow-col text-md w-full justify-center divide-x-[1px] divide-gray-400">
      {#each navItems as item}
        <li class={twMerge(`px-4 text-center uppercase hover:underline ${item.mobileOnly ? 'lg:hidden' : ''}`)}>
          <a
            href={item.path}
            target={item.path.startsWith('https://') ? '_blank' : null}
            rel={item.path.startsWith('https://') ? 'noopener noreferrer' : null}
          >
            {$t(item.label)}
          </a>
        </li>
      {/each}
    </ul>
  </nav>
</header>
